import { getApp, getApps, initializeApp } from "firebase/app";
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import FirebaseConfig, { FCM_VAPID_KEY } from "configs/FirebaseConfig";
import NotificationService from "services/NotificationService";

const PushNotificationService = {};

const SW_URL = "/firebase-messaging-sw.js";
// Dedicated scope: the worker never controls the app pages, so serviceWorker.unregister() in src/index.js leaves it alone
const SW_SCOPE = "/firebase-cloud-messaging-push-scope";
const TOKEN_STORAGE_KEY = "push_token";

const readStoredToken = () => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const storeToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch (e) {
    // Storage unavailable (private window, blocked site data)
  }
};

// src/auth/FirebaseAuth.js may already have initialized the default app through the compat SDK
const getMessagingInstance = () => {
  const app = getApps().length ? getApp() : initializeApp(FirebaseConfig);
  return getMessaging(app);
};

// The worker reads the Firebase config from its query string
const registerWorker = () =>
  navigator.serviceWorker.register(`${SW_URL}?${new URLSearchParams(FirebaseConfig)}`, { scope: SW_SCOPE });

PushNotificationService.isSupported = async function () {
  if (!FCM_VAPID_KEY || typeof window === "undefined" || !("Notification" in window) || !("serviceWorker" in navigator)) {
    return false;
  }
  try {
    return await isSupported();
  } catch (e) {
    return false;
  }
};

// Needs Notification.permission === "granted". Safe to call on every load: the backend upserts the token.
PushNotificationService.enable = async function () {
  const serviceWorkerRegistration = await registerWorker();
  const token = await getToken(getMessagingInstance(), { vapidKey: FCM_VAPID_KEY, serviceWorkerRegistration });
  console.log("token", token);
  if (!token) {
    return null;
  }
  const response = await NotificationService.registerPushToken(token);
  console.log("response", response);
  storeToken(token);
  return token;
};

// Called on logout, while the auth cookie is still valid. Never throws so logout is not blocked.
PushNotificationService.disable = async function () {
  const token = readStoredToken();
  if (!token) {
    return;
  }
  storeToken(null);
  try {
    await NotificationService.unregisterPushToken(token);
  } catch (e) {
    // The token is also invalidated below, so the backend drops it on its next send
  }
  try {
    await deleteToken(getMessagingInstance());
  } catch (e) {
    console.error("Push token deletion failed:", e);
  }
};

// Messages received while a dashboard tab is visible. Returns the unsubscribe function.
PushNotificationService.onForegroundMessage = function (callback) {
  return onMessage(getMessagingInstance(), callback);
};

export default PushNotificationService;
